// #23 (support) — menu « Applications » : passer d'une app de l'écosystème à
// l'autre sans perdre la session de l'onglet courant (SSO partagé).
//
// JS pur, sans React ni `import.meta.env` : testé sous Node par
// `api/scripts/testAppSwitcher.ts`. Les URLs sont injectées par l'appelant
// (variables `VITE_*_URL`, différentes en staging et en prod).
//
// Périmètre : Synolog et Synosight. Synoplace et Synobatch sont volontairement
// absents (décision du 2026-10-29) ; Assets n'entrera qu'une fois son SSO
// confirmé et un moyen de vérifier l'accès disponible.

const ACCESS_PATH = '/api/auth/access';

const trimSlash = (url) => String(url).replace(/\/+$/, '');

/**
 * Liste déclarative des apps. `accessUrl` : endpoint en LECTURE SEULE de l'app
 * cible (`/auth/access`, jamais `/auth/me` qui crée un compte au passage).
 */
export function buildEcosystemApps({ synologUrl, synosightUrl }) {
  return [
    { key: 'synolog', name: 'Synolog', url: synologUrl, accessUrl: `${trimSlash(synologUrl)}${ACCESS_PATH}` },
    { key: 'synosight', name: 'Synosight', url: synosightUrl, accessUrl: `${trimSlash(synosightUrl)}${ACCESS_PATH}` },
  ];
}

/** Nom de fenêtre stable par app : permet de retrouver l'onglet déjà ouvert. */
export function windowNameFor(key) {
  return `synotis-${key}`;
}

/**
 * Lit la réponse de `/auth/access`. `undefined` = on n'en conclut rien (réseau
 * coupé, endpoint pas encore déployé) : l'app reste listée, comme le raccourci
 * historique.
 */
export function accessFromResponse(data) {
  if (!data || typeof data !== 'object' || !('hasAccess' in data)) return undefined;
  return data.hasAccess === true;
}

/**
 * Apps à afficher. L'app courante est toujours listée (marquée `isCurrent`) ;
 * les autres le sont pour un admin, ou tant que l'app cible ne refuse pas
 * explicitement l'accès (`access[key] === false`).
 */
export function visibleApps(apps, { currentKey, isAdmin, access }) {
  return apps
    .filter((app) => app.key === currentKey || isAdmin || access?.[app.key] !== false)
    .map((app) => ({ ...app, isCurrent: app.key === currentKey }));
}

/** Clic gauche sans modificateur : les autres (milieu, Cmd/Ctrl…) restent au navigateur. */
export function isPlainLeftClick(event) {
  return (
    (event.button ?? 0) === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

/**
 * Ouvre l'app dans SON onglet nommé, à appeler uniquement dans un gestionnaire
 * de clic (sinon bloqueur de popups). Renvoie 'focused' (onglet existant mis au
 * premier plan, sans rechargement), 'opened' (nouvel onglet) ou 'blocked'.
 *
 * `opened` : Map key → fenêtre, références des onglets ouverts depuis cet
 * onglet. Perdue au rechargement : on retrouve alors l'onglet par son nom avec
 * `open('', nom)`, qui renvoie l'onglet existant SANS le naviguer, ou crée un
 * onglet `about:blank` (même origine, lisible) qu'on envoie vers l'app. Un
 * onglet de l'app cible est cross-origin : lire son `location.href` lève.
 */
export function openEcosystemApp(app, { win, opened }) {
  const name = windowNameFor(app.key);
  const known = opened.get(app.key);
  if (known && !known.closed) {
    known.focus();
    return 'focused';
  }
  const tab = win.open('', name);
  if (!tab) return 'blocked';
  opened.set(app.key, tab);
  let isBlank = false;
  try {
    isBlank = tab.location.href === 'about:blank';
  } catch {
    // Cross-origin : c'est l'onglet de l'app, déjà ouvert.
  }
  if (isBlank) {
    tab.location.replace(app.url);
    return 'opened';
  }
  tab.focus();
  return 'focused';
}
